/** 项目素材管理；一图一落地页，统一比例缩略图可放大，卡片下方展示关联落地页状态，私有预览对象 URL 按生命周期释放。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type ProjectAsset,
  type ProjectDetail,
  type ProjectMutation,
  landingProjectQueryKey,
  loadLandingProjectAssets,
  useLandingProjectAssets,
} from '~/data/queries';
import { landingAssetsApi } from '~/hooks/use-landing-assets';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { CopyableCode } from '~/ui/CopyableCode';
import { ConfirmDialog } from '~/ui/Dialog';
import { Pagination } from '~/ui/Pagination';
import { toast } from '~/ui/Toast';
import { LandingAssetImage } from './LandingAssetImage';
import { LandingPageDeliveryChip, LandingPageDownloadChip } from './LandingPageDeliveryCells';
import { ProjectState } from './landing-project-display';
type UploadItem = {
  id: string;
  file: File;
  width: number;
  height: number;
  status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'DUPLICATE';
  error: string | null;
  r2Key?: string;
  allowDuplicate?: boolean;
  duplicate?: boolean;
  contentHash?: string;
};
function imageRatio(width: number, height: number) {
  let divisor = width;
  let remainder = height;
  while (remainder) {
    const next = divisor % remainder;
    divisor = remainder;
    remainder = next;
  }
  return `${width / divisor}:${height / divisor}`;
}
export function LandingProjectMediaPanel({
  project,
  onChanged,
  onExecution,
  onOpenLandingPages,
}: {
  project: ProjectDetail;
  onChanged: (result: ProjectMutation) => void;
  onExecution: (id: string) => void;
  /** 切到「HTML 落地页」Tab 并定位到该素材关联的落地页。 */
  onOpenLandingPages: (taskId: string) => void;
}) {
  const [page, setPage] = useState(1);
  const client = useQueryClient();
  const query = useLandingProjectAssets(project.id, page);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [picking, setPicking] = useState(false);
  const [limitedDuplicateCheck, setLimitedDuplicateCheck] = useState(false);
  const [remove, setRemove] = useState<ProjectAsset | null>(null);
  const [selected, setSelected] = useState(new Set<string>());
  const [generateScope, setGenerateScope] = useState<'selected' | 'all' | null>(null);
  // 后端拒绝给归档项目添加素材；上传前就拦下，免得图片先传进 R2、绑定时才失败。
  const uploadBlockReason =
    project.status === 'ARCHIVED' ? '项目已归档，请先恢复项目再上传素材' : null;
  // 下架原因已在面板顶部常驻展示；这里只解释模板与文案前置条件，避免按钮无故禁用。
  const generationBlockReason = !project.can_generate
    ? (project.generation_blocked_reason ?? '当前书籍不能投放')
    : !project.template_id
      ? '请先在「HTML 模板」中选择模板'
      : !project.copy_overrides
        ? '请先在「HTML 模板」中保存页面文案'
        : null;
  const generation = useMutation({
    mutationFn: async (assetIds: string[] | null) => {
      if (!project.can_generate)
        throw new Error(project.generation_blocked_reason ?? '当前书籍不能投放');
      const assets = await loadLandingProjectAssets(project.id);
      const scope = assetIds ? assets.filter((asset) => assetIds.includes(asset.id)) : assets;
      if (assetIds && scope.length !== assetIds.length)
        throw new Error('所选素材已被移除，请刷新后重新选择');
      if (!scope.length) throw new Error('没有可生成的素材');
      const taskIds = scope.flatMap((asset) => (asset.task_id ? [asset.task_id] : []));
      if (taskIds.length !== scope.length) throw new Error('部分素材尚未绑定任务，请刷新后重试');
      return landingProjectsApi.execute(project.id, { operation: 'generate', task_ids: taskIds });
    },
    onSuccess: (execution) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      onExecution(execution.id);
      toast('素材对应任务已提交后台生成，请在完成后重新下载', { tone: 'info' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const selectedGenerationBlockReason =
    generationBlockReason ??
    (generation.isPending ? '正在提交生成任务，请稍候' : !selected.size ? '请先勾选素材' : null);
  const allGenerationBlockReason =
    generationBlockReason ??
    (generation.isPending ? '正在提交生成任务，请稍候' : !query.data?.total ? '暂无素材' : null);
  const upload = useMutation({
    mutationFn: async (items: UploadItem[]) => {
      const existing = await loadLandingProjectAssets(project.id);
      setLimitedDuplicateCheck(existing.some((asset) => !asset.content_sha256));
      const knownHashes = new Set(
        existing.flatMap((asset) => (asset.content_sha256 ? [asset.content_sha256] : [])),
      );
      for (const item of items) {
        setUploads((current) =>
          current.map((row) =>
            row.id === item.id ? { ...row, status: 'RUNNING', error: null, duplicate: false } : row,
          ),
        );
        let r2Key = item.r2Key;
        try {
          const contentHash =
            item.contentHash ??
            Array.from(
              new Uint8Array(await crypto.subtle.digest('SHA-256', await item.file.arrayBuffer())),
              (byte) => byte.toString(16).padStart(2, '0'),
            ).join('');
          setUploads((current) =>
            current.map((row) => (row.id === item.id ? { ...row, contentHash } : row)),
          );
          if (!r2Key && !item.allowDuplicate && knownHashes.has(contentHash)) {
            setUploads((current) =>
              current.map((row) =>
                row.id === item.id
                  ? {
                      ...row,
                      status: 'DUPLICATE',
                      duplicate: true,
                      error: '相同内容素材已存在；如需另一独立任务，可明确继续添加',
                    }
                  : row,
              ),
            );
            continue;
          }
          if (!r2Key) {
            const result = await landingAssetsApi.upload(item.file, 'banner');
            r2Key = result.r2_key;
            setUploads((current) =>
              current.map((row) => (row.id === item.id ? { ...row, r2Key } : row)),
            );
          }
          const result = await landingProjectsApi.addAsset(
            project.id,
            {
              name: item.file.name,
              asset: { r2_key: r2Key },
              bytes: item.file.size,
              width: item.width,
              height: item.height,
              content_type: item.file.type,
            },
            item.id,
          );
          setUploads((current) =>
            current.map((row) => (row.id === item.id ? { ...row, status: 'SUCCEEDED' } : row)),
          );
          knownHashes.add(contentHash);
          onChanged(result);
        } catch (error) {
          setUploads((current) =>
            current.map((row) =>
              row.id === item.id
                ? {
                    ...row,
                    status: 'FAILED',
                    error: error instanceof Error ? error.message : '上传或绑定失败',
                  }
                : row,
            ),
          );
        }
      }
    },
    onError: (error: Error) => {
      setUploads((current) =>
        current.map((row) =>
          row.status === 'PENDING' ? { ...row, status: 'FAILED', error: error.message } : row,
        ),
      );
      toast(error.message, { tone: 'neg' });
    },
  });
  const deletion = useMutation({
    mutationFn: (asset: ProjectAsset) => landingProjectsApi.removeAsset(project.id, asset.id),
    onSuccess: (result, asset) => {
      setSelected((current) => {
        const next = new Set(current);
        next.delete(asset.id);
        return next;
      });
      onChanged(result);
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const pick = async (files: File[]) => {
    setPicking(true);
    const valid: UploadItem[] = [];
    for (const file of files) {
      if (
        !['image/webp', 'image/png', 'image/jpeg'].includes(file.type) ||
        file.size < 1 ||
        file.size > 2 * 1024 * 1024
      ) {
        toast(`${file.name}：请选择 ≤2MB 的 webp/png/jpeg`, { tone: 'neg' });
        continue;
      }
      let bitmap: ImageBitmap | null = null;
      try {
        bitmap = await createImageBitmap(file);
        if (bitmap.width < 1 || bitmap.height < 1 || bitmap.width > 50000 || bitmap.height > 50000)
          throw new Error('图片尺寸不在支持范围内');
        valid.push({
          id: crypto.randomUUID(),
          file,
          width: bitmap.width,
          height: bitmap.height,
          status: 'PENDING',
          error: null,
        });
      } catch {
        toast(`${file.name}：无法解码图片或尺寸不受支持，请重新选择有效图片`, { tone: 'neg' });
      } finally {
        bitmap?.close();
      }
    }
    setUploads((current) => [...current, ...valid]);
    setPicking(false);
    if (valid.length) upload.mutate(valid);
  };
  return (
    <div className="vstack" style={{ gap: 12 }}>
      {!project.can_generate && (
        <p role="alert">{project.generation_blocked_reason ?? '当前书籍不能投放'}</p>
      )}
      <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
        <label
          className="btn btn-accent"
          aria-disabled={uploadBlockReason ? true : undefined}
          title={uploadBlockReason ?? undefined}
        >
          {picking ? '读取图片尺寸…' : '上传多张素材'}
          <input
            type="file"
            aria-label="上传多张投放素材"
            multiple
            accept="image/webp,image/png,image/jpeg"
            hidden
            disabled={!!uploadBlockReason || upload.isPending || picking}
            onChange={(event) => {
              void pick(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
        </label>
        {/* 禁用按钮不接收指针事件，由外层承载悬停提示，避免浏览器吞掉 title。 */}
        <span
          title={selectedGenerationBlockReason ?? undefined}
          style={{
            display: 'inline-flex',
            cursor: selectedGenerationBlockReason ? 'not-allowed' : undefined,
          }}
        >
          <button
            type="button"
            className="btn"
            disabled={!!selectedGenerationBlockReason}
            title={selectedGenerationBlockReason ?? undefined}
            style={{ pointerEvents: selectedGenerationBlockReason ? 'none' : undefined }}
            onClick={() => setGenerateScope('selected')}
          >
            生成已选 {selected.size} 张素材
          </button>
        </span>
        <span
          title={allGenerationBlockReason ?? undefined}
          style={{
            display: 'inline-flex',
            cursor: allGenerationBlockReason ? 'not-allowed' : undefined,
          }}
        >
          <button
            type="button"
            className="btn"
            disabled={!!allGenerationBlockReason}
            title={allGenerationBlockReason ?? undefined}
            style={{ pointerEvents: allGenerationBlockReason ? 'none' : undefined }}
            onClick={() => setGenerateScope('all')}
          >
            生成全部 {query.data?.total ?? 0} 张素材
          </button>
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() =>
            setSelected((current) => {
              const next = new Set(current);
              for (const asset of query.data?.data ?? []) next.add(asset.id);
              return next;
            })
          }
        >
          全选当前页素材
        </button>
        {selected.size > 0 && (
          <button type="button" className="btn btn-ghost" onClick={() => setSelected(new Set())}>
            清空选择
          </button>
        )}
        <span className="muted">每张 ≤2MB · 一张图片对应一个 HTML 落地页 · 生成需明确提交</span>
      </div>
      {uploadBlockReason && (
        <p role="note" style={{ color: 'var(--warn)' }}>
          {uploadBlockReason}
        </p>
      )}
      {project.can_generate && generationBlockReason && (
        <p className="muted">{generationBlockReason}，之后才能生成素材对应的 HTML。</p>
      )}
      {limitedDuplicateCheck && (
        <p className="muted">部分历史素材缺少内容指纹，重复检查仅覆盖已记录指纹的素材。</p>
      )}
      {uploads.map((item) => (
        <div
          className="card-flat hstack"
          key={item.id}
          style={{ padding: 10, gap: 10, flexWrap: 'wrap' }}
        >
          <span>{item.file.name}</span>
          <span className="mono muted">
            {item.width}×{item.height} px · 比例 {imageRatio(item.width, item.height)}
          </span>
          <ProjectState status={item.status} />
          {item.error && (
            <span role="alert" style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}>
              {item.error}
            </span>
          )}
          {(item.status === 'FAILED' || item.status === 'DUPLICATE') && (
            <button
              type="button"
              className="btn btn-sm"
              disabled={!!uploadBlockReason || upload.isPending}
              title={uploadBlockReason ?? undefined}
              onClick={() =>
                upload.mutate([{ ...item, allowDuplicate: item.duplicate || item.allowDuplicate }])
              }
            >
              {item.duplicate ? '仍添加重复素材' : '重试失败项'}
            </button>
          )}
        </div>
      ))}
      {query.isPending ? (
        <p>加载素材…</p>
      ) : query.isError ? (
        <div className="card" role="alert" style={{ padding: 18 }}>
          <p>{query.error.message}</p>
          <button type="button" className="btn btn-sm" onClick={() => void query.refetch()}>
            重新加载
          </button>
        </div>
      ) : !query.data?.data.length ? (
        <div className="card" style={{ padding: 24 }}>
          暂无媒体素材。纯文字模板无需素材，系统为项目提供纯文字落地页。
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,240px),1fr))',
            gap: 12,
          }}
        >
          {query.data.data.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selected.has(asset.id)}
              onToggle={() =>
                setSelected((current) => {
                  const next = new Set(current);
                  if (next.has(asset.id)) next.delete(asset.id);
                  else next.add(asset.id);
                  return next;
                })
              }
              onRemove={() => setRemove(asset)}
              onOpenLandingPages={onOpenLandingPages}
            />
          ))}
        </div>
      )}
      <Pagination total={query.data?.total ?? 0} page={page} pageSize={20} onChange={setPage} />
      {generateScope && (
        <ConfirmDialog
          title="按素材生成 HTML"
          message={`将生成${generateScope === 'all' ? `项目全部 ${query.data?.total ?? 0}` : `已选 ${selected.size}`} 张素材对应的 HTML 落地页，不重复创建落地页。完成后请重新下载。`}
          confirmLabel="提交生成"
          onConfirm={() => generation.mutate(generateScope === 'all' ? null : [...selected])}
          onClose={() => setGenerateScope(null)}
        />
      )}
      {remove && (
        <ConfirmDialog
          title="移除此项目素材？"
          message="将解除素材与当前项目的关联，相关 HTML 输入会失效。已发布版本与历史记录保留。"
          confirmLabel="移除"
          onConfirm={() => deletion.mutate(remove)}
          onClose={() => setRemove(null)}
        />
      )}
    </div>
  );
}

/** 单张素材卡：名称只显示一次并截断，缩略图统一 4:3 留白展示，下方是关联落地页的生成 / 投放 / 下载状态。 */
function AssetCard({
  asset,
  selected,
  onToggle,
  onRemove,
  onOpenLandingPages,
}: {
  asset: ProjectAsset;
  selected: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onOpenLandingPages: (taskId: string) => void;
}) {
  const meta = [
    asset.width && asset.height
      ? `${asset.width}×${asset.height} · ${imageRatio(asset.width, asset.height)}`
      : null,
    asset.bytes === null ? null : `${Math.ceil(asset.bytes / 1024)} KB`,
  ].filter(Boolean);
  const task = asset.task;
  return (
    <div
      className="card vstack"
      style={{
        padding: 10,
        gap: 8,
        minWidth: 0,
        borderColor: selected ? 'var(--accent)' : undefined,
      }}
    >
      <label className="hstack" style={{ gap: 8, minWidth: 0 }} title={asset.name}>
        <input
          type="checkbox"
          aria-label={`选择素材 ${asset.name}`}
          checked={selected}
          onChange={onToggle}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 500,
          }}
        >
          {asset.name}
        </span>
      </label>
      <LandingAssetImage
        r2Key={asset.asset.r2_key}
        name={asset.name}
        aspectRatio="4 / 3"
        zoomable
      />
      {meta.length > 0 && (
        <div className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
          {meta.join(' · ')}
        </div>
      )}
      <div
        className="vstack"
        style={{ gap: 6, paddingTop: 8, borderTop: '1px solid var(--border)' }}
      >
        {task ? (
          <>
            <div className="hstack" style={{ gap: 6, minWidth: 0 }}>
              <span className="muted" style={{ fontSize: 'var(--text-xs)', flexShrink: 0 }}>
                关联落地页
              </span>
              <CopyableCode value={task.task_id} label="落地页编号" />
            </div>
            <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
              {/* 生成中时旧指纹仍在、stale_reason 也有值；先认 GENERATING，否则会误显「待重新生成」。 */}
              <ProjectState
                status={
                  task.generate_status === 'GENERATING'
                    ? 'GENERATING'
                    : task.stale_reason
                      ? 'STALE'
                      : task.generate_status
                }
              />
              <LandingPageDeliveryChip
                status={task.delivery_status}
                outdated={task.delivery_outdated}
              />
              <LandingPageDownloadChip count={task.download_count} />
            </div>
          </>
        ) : (
          <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            尚未关联落地页
          </span>
        )}
        <div className="hstack" style={{ gap: 4 }}>
          {task && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => onOpenLandingPages(task.id)}
            >
              查看落地页
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-sm btn-ghost" onClick={onRemove}>
            移除素材
          </button>
        </div>
      </div>
    </div>
  );
}
